import React, { useState, useEffect } from 'react';
import { TablePaginationConfig } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { useSetRecoilState } from 'recoil';
import { useHistory, useLocation } from 'react-router';
import { useParams } from 'react-router-dom';
import { useUpdateEffect } from 'usehooks-ts';

import './index.less';

import { getPageFromSearchParams, PAGE_INDEX_PARAM } from '../../../../utils/urlUtil';
import { alertsSelectorAdd } from '../../../../recoil';
import {
  ELMA365ToolsetAvailability,
  ELMA365ToolsetModel,
  ELMA365ToolsetVisibility,
  ELMA365VersionAction,
  ELMA365VersionModel,
} from '../../../../../gamma-api';
import { defaultApi } from '../../../../apis';
import { getTranslateFunc } from '../../../../utils/stringUtil';
import { getToolkitPath } from '../../../../utils/pathUtils';
import { ToolsetsEmptyLogo } from '../../../../assets';
import IbIcon from '../../../../components/common/IbIcon';
import IbTypography from '../../../../components/common/IbTypography';
import IbButton, { IIbButtonAction } from '../../../../components/common/IbButton';
import IbPageTitle from '../../../../components/IbPageTitle';
import IbPagination from '../../../../components/common/IbPagination';
import IbSwitch from '../../../../components/common/IbSwitch';
import IbTable from '../../../../components/common/IbTable';
import IbContextMenu, { IIbContextMenuItem } from '../../../../components/common/IbContextMenu';
import IbTextHighlighter from '../../../../components/common/IbTextHighlighter';
import ImportUpload from '../../../../components/ImportUpload';
import ExportButton from '../../../../components/ExportButton';

import {
  CELL_SWITCH_CLASS_NAME,
  CONTENT_CLASS_NAME,
  EMPTY_CONTENT_CLASS_NAME,
  EMPTY_CONTENT_PLACEHOLDER_CLASS_NAME,
  MAIN_CLASS_NAME,
  NOTHING_FOUND_PLACEHOLDER_CLASS_NAME,
  PAGINATION_ROW_CLASS_NAME,
  TOOLBAR_CLASSNAME,
  TOOLSETS_PAGE_SIZE,
} from './const';
import ToolsetModal from './ToolsetModal';
import VersionMerge from './VersionMerge';
import ConfirmModal from './ConfirmModal';

const ToolsetsPage: React.FC = () => {
  const translate = getTranslateFunc('Toolkits: ELMA365: Toolsets:');

  const location = useLocation();
  const { push } = useHistory();

  const { folderId } = useParams<{ folderId: string }>();

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: getPageFromSearchParams(location.search),
    pageSize: TOOLSETS_PAGE_SIZE,
    total: 0,
  });

  const [searchText, setSearchText] = useState('');
  const [toolsets, setToolsets] = useState<ELMA365ToolsetModel[]>([]);
  const [action, setAction] = useState<ELMA365VersionAction>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [versionMergeVisible, setVersionMergeVisible] = useState(false);
  const [toolsetModalVisible, setToolsetModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const [toggleToolset, setToggleToolset] = useState<ELMA365ToolsetModel>();
  const [contextToolset, setContextToolset] = useState<ELMA365ToolsetModel>();
  const [mergeToolset, setMergeToolset] = useState<ELMA365ToolsetModel>();
  const [modalToolset, setModalToolset] = useState<ELMA365ToolsetModel>();
  const [replaceToolset, setReplaceToolset] = useState<ELMA365ToolsetModel>();
  const [deleteToolset, setDeleteToolset] = useState<ELMA365ToolsetModel>();

  const toolsetListIsEmpty = !toolsets.length;
  const showEmptyList = toolsetListIsEmpty && !loading;

  const goToToolset = (id: string) => {
    push(getToolkitPath(folderId, id));
  };

  const loadToolsets = async (withLoading: boolean) => {
    withLoading && setLoading(true);

    try {
      const response = await defaultApi.searchELMA365ToolkitToolsets(
        (pagination.current || 1) - 1,
        TOOLSETS_PAGE_SIZE,
        true,
        searchText,
        [folderId],
        undefined,
        undefined,
        undefined,
        [ELMA365ToolsetVisibility.Visible]
      );
      setToolsets(response.data.items || []);
      setPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({
        content: translate('Failed to load'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }

    withLoading && setLoading(false);
  };

  const toggleAvailability = async (toolset: ELMA365ToolsetModel) => {
    setSaving(true);
    setToggleToolset(toolset);

    try {
      await defaultApi.toggleELMA365ToolkitToolset(toolset.toolset_id, {
        availability:
          toolset.availability === ELMA365ToolsetAvailability.Enabled
            ? ELMA365ToolsetAvailability.Disabled
            : ELMA365ToolsetAvailability.Enabled,
      });
    } catch (err) {
      addAlert({ content: translate('Updating error'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }

    await loadToolsets(false);
    setSaving(false);
    setToggleToolset(undefined);
  };

  useEffect(() => {
    loadToolsets(true).finally();
  }, [folderId, pagination.current, pagination.pageSize, searchText]);

  useEffect(() => {
    const page = getPageFromSearchParams(location.search);
    if (page !== pagination.current) {
      setPagination((prev) => ({ ...prev, current: page }));
    }
  }, [location.search]);

  useUpdateEffect(() => {
    if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [searchText]);

  useUpdateEffect(() => {
    if (pagination.current) {
      const params = new URLSearchParams(location.search);
      params.set(PAGE_INDEX_PARAM, pagination.current.toString());
      push({ pathname: location.pathname, search: params.toString() });
    }
  }, [pagination.current]);

  useEffect(() => {
    if (searchText) {
      setSearchText('');
    } else if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [folderId]);

  const onCreateToolsetClick = () => {
    setAction(ELMA365VersionAction.Creation);
    setToolsetModalVisible(true);
  };

  const onImportFileFinish = async (toolset: ELMA365ToolsetModel) => {
    goToToolset(toolset.toolset_id);
  };

  const onVersionMergeFinish = (version: ELMA365VersionModel) => {
    setVersionMergeVisible(false);
    setMergeToolset(undefined);
    goToToolset(version.toolset_id);
  };

  const onVersionMergeCancel = () => {
    setVersionMergeVisible(false);
    setMergeToolset(undefined);
  };

  const onToolsetModalFinish = async (toolset: ELMA365ToolsetModel) => {
    setToolsetModalVisible(false);
    setModalToolset(undefined);
    setAction(undefined);
    if (action) {
      goToToolset(toolset.toolset_id);
    } else {
      await loadToolsets(true);
    }
  };

  const onToolsetModalCancel = () => {
    setToolsetModalVisible(false);
    setModalToolset(undefined);
    setAction(undefined);
  };

  const onReplaceConfirmButtonClick = async () => {
    setAction(ELMA365VersionAction.Creation);
    setModalToolset(replaceToolset);
    setReplaceToolset(undefined);
    setToolsetModalVisible(true);
  };

  const onReplaceCancelButtonClick = () => {
    setReplaceToolset(undefined);
  };

  const onDeleteConfirmButtonClick = async () => {
    if (!deleteToolset) {
      return;
    }

    setDeleteConfirmLoading(true);

    try {
      await defaultApi.deleteELMA365ToolkitToolset(deleteToolset.toolset_id);
    } catch (e) {
      addAlert({
        content: translate('Deletion error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
    }

    await loadToolsets(false);
    setDeleteConfirmLoading(false);
    setDeleteToolset(undefined);
  };

  const onDeleteCancelButtonClick = () => {
    setDeleteToolset(undefined);
  };

  const onPaginationChange = (page: number) => setPagination({ ...pagination, current: page });

  const onSearchTextChange = (value: string) => setSearchText(value);

  const renderNameCell = (toolset: ELMA365ToolsetModel) => {
    return (
      <IbTypography>
        <IbTypography.Paragraph
          disabled={toolset.availability === ELMA365ToolsetAvailability.Disabled}
          type="secondary"
        >
          <IbTextHighlighter highlightedText={searchText} text={toolset.name} />
        </IbTypography.Paragraph>
      </IbTypography>
    );
  };

  const renderNamespacesCell = (toolset: ELMA365ToolsetModel) => {
    return (
      <IbTypography>
        {toolset.namespaces.map((namespace) => (
          <IbTypography.Paragraph
            key={namespace.entity_id}
            disabled={toolset.availability === ELMA365ToolsetAvailability.Disabled}
            type="secondary"
          >
            <IbTextHighlighter highlightedText={searchText} text={namespace.identity.name} />
          </IbTypography.Paragraph>
        ))}
      </IbTypography>
    );
  };

  const renderVolumesCell = (toolset: ELMA365ToolsetModel) => {
    return (
      <IbTypography>
        {toolset.namespaces.map((namespace) => (
          <IbTypography.Paragraph
            key={namespace.entity_id}
            disabled={toolset.availability === ELMA365ToolsetAvailability.Disabled}
            type="secondary"
          >
            {translate('Table: Tools: Count', { count: namespace.volume.tool_count })}
          </IbTypography.Paragraph>
        ))}
      </IbTypography>
    );
  };

  const renderAvailabilitySwitch = (toolset: ELMA365ToolsetModel) => {
    const enabled = toolset.availability === ELMA365ToolsetAvailability.Enabled;
    const processing = saving && toggleToolset?.toolset_id === toolset.toolset_id;

    const onSwitchChange = async (_: boolean, event: MouseEvent) => {
      event.stopPropagation();
      await toggleAvailability(toolset);
    };

    const onCellSwitchClick = async (event: React.MouseEvent) => {
      event.stopPropagation();
      await toggleAvailability(toolset);
    };

    return (
      <IbTypography>
        <IbTypography.Paragraph disabled={!enabled} type="secondary">
          <div className={CELL_SWITCH_CLASS_NAME} onClick={onCellSwitchClick}>
            <IbSwitch checked={enabled} loading={processing} size="small" onChange={onSwitchChange} />
            <span>{enabled ? translate('Availability: Enabled') : translate('Availability: Disabled')}</span>
          </div>
        </IbTypography.Paragraph>
      </IbTypography>
    );
  };

  const renderContextMenu = (toolset: ELMA365ToolsetModel) => {
    const onMergeClick = () => {
      setMergeToolset(toolset);
      setVersionMergeVisible(true);
    };

    const onUpdateClick = () => {
      setAction(undefined);
      setModalToolset(toolset);
      setToolsetModalVisible(true);
    };

    const onReplaceClick = () => {
      setReplaceToolset(toolset);
    };

    const onDeleteClick = () => setDeleteToolset(toolset);

    const onMenuOpen = () => setContextToolset(toolset);

    const onMenuClose = () => setContextToolset(undefined);

    const menuItems: IIbContextMenuItem[] = [
      {
        icon: <IbIcon iconName="refresh" />,
        text: translate('Context: Merge'),
        onSelect: onMergeClick,
      },
      {
        icon: <IbIcon iconName="edit" />,
        text: translate('Context: Update'),
        onSelect: onUpdateClick,
      },
      {
        icon: <IbIcon iconName="setting-two" />,
        text: translate('Context: Replace'),
        onSelect: onReplaceClick,
      },
      {
        icon: <IbIcon iconName="efferent-three" />,
        text: translate('Context: Export'),
        render: (child) => (
          <ExportButton.ToolkitExport toolsetId={toolset.toolset_id}>{child}</ExportButton.ToolkitExport>
        ),
      },
      {
        icon: <IbIcon iconName="afferent-three" />,
        text: translate('Context: Import'),
        render: (child: React.ReactNode) => (
          <ImportUpload.ToolkitImport
            folderId={folderId}
            toolsetId={toolset.toolset_id}
            onFinish={onToolsetModalFinish}
          >
            {child}
          </ImportUpload.ToolkitImport>
        ),
      },
      {
        icon: <IbIcon iconName="delete" />,
        text: translate('Context: Delete'),
        onSelect: onDeleteClick,
      },
    ];

    return (
      <IbContextMenu
        menuItems={menuItems}
        visible={contextToolset?.toolset_id === toolset.toolset_id}
        onClose={onMenuClose}
      >
        <IbButton icon={<IbIcon iconName="more-one" />} type="icon" onClick={onMenuOpen}></IbButton>
      </IbContextMenu>
    );
  };

  const renderToolbar = () => {
    if (showEmptyList) {
      return (
        <div className={TOOLBAR_CLASSNAME}>
          <IbButton icon={<IbIcon iconName="add-one" />} onClick={onCreateToolsetClick}>
            {translate('Toolbar: Create')}
          </IbButton>
          <ImportUpload.ToolkitImport folderId={folderId} onFinish={onImportFileFinish}>
            <IbButton icon={<IbIcon iconName="afferent-three" />} type="secondary">
              {translate('Toolbar: Import')}
            </IbButton>
          </ImportUpload.ToolkitImport>
        </div>
      );
    }

    const actions: IIbButtonAction[] = [
      {
        icon: <IbIcon iconName="afferent-three" />,
        text: translate('Toolbar: Import'),
        render: (child) => (
          <ImportUpload.ToolkitImport folderId={folderId} onFinish={onImportFileFinish}>
            {child}
          </ImportUpload.ToolkitImport>
        ),
      },
    ];

    return (
      <div className={TOOLBAR_CLASSNAME}>
        <IbButton actions={actions} icon={<IbIcon iconName="add-one" />} onClick={onCreateToolsetClick}>
          {translate('Toolbar: Create')}
        </IbButton>
      </div>
    );
  };

  const renderToolsetTable = () => {
    const columns: ColumnType<ELMA365ToolsetModel>[] = [
      {
        title: translate('Table: Name'),
        width: '25%',
        render: (_, toolset) => renderNameCell(toolset),
      },
      {
        title: translate('Table: Namespaces'),
        width: '25%',
        render: (_, toolset) => renderNamespacesCell(toolset),
      },
      {
        title: translate('Table: Tools'),
        width: '25%',
        render: (_, toolset) => renderVolumesCell(toolset),
      },
      {
        title: translate('Table: Availability'),
        width: '25%',
        render: (_, toolset) => renderAvailabilitySwitch(toolset),
      },
      {
        title: '',
        render: (_, toolset) => renderContextMenu(toolset),
      },
    ];

    const onRowClick = (toolset: ELMA365ToolsetModel, column: ColumnType<unknown>) => {
      if (column.title) {
        goToToolset?.(toolset.toolset_id);
      }
    };

    return (
      <>
        {renderToolbar()}
        <IbTable
          columns={columns}
          dataSource={toolsets}
          emptyText={<IbTypography.Paragraph disabled>{translate('Nothing found')}</IbTypography.Paragraph>}
          loading={loading}
          rowKey={(toolset: ELMA365ToolsetModel) => toolset.toolset_id}
          onRowClick={onRowClick}
        />
      </>
    );
  };

  const renderEmptyList = () => {
    if (searchText) {
      return <div className={NOTHING_FOUND_PLACEHOLDER_CLASS_NAME}>{translate('Nothing found')}</div>;
    }

    return (
      <div className={EMPTY_CONTENT_PLACEHOLDER_CLASS_NAME}>
        <ToolsetsEmptyLogo />
        <IbTypography>
          <IbTypography.Paragraph disabled type="secondary">
            {translate('Empty placeholder: line 1')}
            <br />
            {translate('Empty placeholder: line 2')}
            <br />
            {translate('Empty placeholder: line 3')}
          </IbTypography.Paragraph>
        </IbTypography>
        {renderToolbar()}
      </div>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbPageTitle
        showSearchInput
        searchPlaceholder={translate('Search: Placeholder')}
        searchText={searchText}
        title={translate('Title')}
        onDebouncedSearch={onSearchTextChange}
      />
      <div className={`${CONTENT_CLASS_NAME} ${showEmptyList && !searchText ? EMPTY_CONTENT_CLASS_NAME : ''}`}>
        {showEmptyList ? renderEmptyList() : renderToolsetTable()}
      </div>
      {(pagination.total ?? 0) > TOOLSETS_PAGE_SIZE && (
        <div className={PAGINATION_ROW_CLASS_NAME}>
          <IbPagination
            current={pagination.current}
            defaultCurrent={1}
            defaultPageSize={TOOLSETS_PAGE_SIZE}
            pageSize={TOOLSETS_PAGE_SIZE}
            showSizeChanger={false}
            total={pagination.total}
            onChange={onPaginationChange}
          />
        </div>
      )}
      <ToolsetModal
        action={action}
        editToolset={modalToolset}
        visible={toolsetModalVisible}
        onCancel={onToolsetModalCancel}
        onFinish={onToolsetModalFinish}
      />
      <VersionMerge
        toolset={mergeToolset}
        visible={versionMergeVisible}
        onCancel={onVersionMergeCancel}
        onFinish={onVersionMergeFinish}
      />
      <ConfirmModal.ReplaceToolset
        visible={!!replaceToolset}
        onCancel={onReplaceCancelButtonClick}
        onConfirm={onReplaceConfirmButtonClick}
      />
      <ConfirmModal.DeleteToolset
        loading={deleteConfirmLoading}
        visible={!!deleteToolset}
        onCancel={onDeleteCancelButtonClick}
        onConfirm={onDeleteConfirmButtonClick}
      />
    </div>
  );
};

export default ToolsetsPage;
